import { Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAllLeaguesStandings } from "@/hooks/useAllLeaguesStandings";
import { Button } from "@/components/ui/button";


import { InitialSyncButton } from "@/components/buttons/InitialSyncButton";
import { usePageFocus } from "@/hooks/usePageFocus";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";

interface AllLeaguesStandingsOverviewProps {
  onSelectLeague?: (leagueApiId: number) => void;
  className?: string;
}

export function AllLeaguesStandingsOverview({ 
  onSelectLeague,
  className 
}: AllLeaguesStandingsOverviewProps) {
  const { leaguesStandings, loading, refresh } = useAllLeaguesStandings();

  // Auto-refresh on page focus and network reconnection
  usePageFocus({ 
    onFocus: () => {
      console.log('Page focused - refreshing standings');
      refresh();
    }
  });
  
  useNetworkStatus({
    onReconnect: () => {
      console.log('Network reconnected - refreshing standings');
      refresh();
    }
  });

  const hasAnyData = leaguesStandings.some(league => league.has_data);


  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {!hasAnyData ? (
        <div className="text-center py-8">
          <Trophy size={48} className="mx-auto mb-4 text-gray-medium" />
          <h3 className="font-bold text-lg mb-2">Standings not available</h3>
          <p className="text-gray-medium text-sm mb-4">
            Initial sync of standings is required to view the data.
          </p>
          <InitialSyncButton
            onSyncComplete={refresh}
            className="mb-4"
          />
        </div>
      ) : (
        <div className="grid gap-3">
          {leaguesStandings.map((league) => (
            <div
              key={league.league_id}
              className="flex items-center justify-between p-3 border border-gray-light rounded-lg bg-paper-sketch hover:bg-paper-aged transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                {league.league_logo && (
                  <img
                    src={league.league_logo}
                    alt={`${league.league_name} logo`}
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm truncate">
                    {league.league_name}
                  </h4>
                  <p className="text-xs text-gray-medium">
                    {league.country}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => onSelectLeague?.(league.api_football_id)}
                variant="outline"
                size="sm"
                className="h-8"
              >
                View
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}